/**
 * Analytics API functions
 * アクセス解析用のAPI関数
 */

import { isUserAuthenticated } from '../auth/AuthContext';
import { getAnalyticsConsent } from '../utils/analyticsConsent';

/**
 * 指定URLが自サイト（現在の origin と同じ）の参照元かどうかを返す
 * 開発環境（CloudFront URL）でも正しく判定する
 */
export const isSameOriginReferrer = (referrerUrl: string): boolean => {
  if (!referrerUrl || typeof referrerUrl !== 'string' || !referrerUrl.trim()) {
    return false;
  }
  try {
    return new URL(referrerUrl).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * API GatewayのベースエンドポイントURLを取得
 * posts.tsと同じロジックを使用
 */
const getApiEndpoint = (): string => {
  const endpoint = import.meta.env.VITE_LAMBDA_ENDPOINT;
  if (endpoint) {
    let cleanEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    
    const isCloudFront = cleanEndpoint.includes('cloudfront.net');
    
    if (isCloudFront) {
      return cleanEndpoint;
    }
    
    const urlMatch = cleanEndpoint.match(/amazonaws\.com(\/.*)?$/);
    const pathAfterAws = urlMatch ? (urlMatch[1] || '') : '';
    const hasStage = pathAfterAws.length > 0 && pathAfterAws !== '/';
    
    if (!hasStage) {
      if (import.meta.env.DEV) {
        cleanEndpoint = `${cleanEndpoint}/dev`;
      } else if (import.meta.env.PROD) {
        cleanEndpoint = `${cleanEndpoint}/prod`;
      }
    }
    
    return cleanEndpoint;
  }
  
  const baseUrl = 'https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com';
  if (import.meta.env.DEV) {
    return `${baseUrl}/dev`;
  } else if (import.meta.env.PROD) {
    return `${baseUrl}/prod`;
  }
  return `${baseUrl}/dev`;
};

/**
 * ページビューを記録する
 * 
 * @param pagePath ページのパス（例: /ja/posts/my-first-post）
 * @param previousPath 遷移前のパス（例: /ja、オプション）
 * @param externalReferrer 外部リファラー（リダイレクト前の外部サイトからのアクセス時に使用、オプション）
 * @returns 成功時はvoid
 */
export const trackPageView = async (pagePath: string, previousPath?: string, externalReferrer?: string): Promise<void> => {
  const endpoint = getApiEndpoint();
  // CloudFront経由の場合は/api/track-access、直接API Gatewayの場合は/track-access
  // 注意: endpointに既に/apiが含まれている場合は追加しない
  const isCloudFront = endpoint.includes('cloudfront.net');
  let url = '';
  if (isCloudFront) {
    // CloudFront経由の場合、endpointに既に/apiが含まれている可能性がある
    if (endpoint.endsWith('/api')) {
      url = `${endpoint}/track-access`;
    } else if (endpoint.endsWith('/api/')) {
      url = `${endpoint}track-access`;
    } else {
      url = `${endpoint}/api/track-access`;
    }
  } else {
    url = `${endpoint}/track-access`;
  }
  
  // 参照元は React 側でだけ決める（document.referrer は使わない）
  // 1. ルート直後のみ: externalReferrer が渡され、かつ previousPath が '' または '/' のときだけ使用（ブラウザバックでは previousPath が入るため使わない）
  // 2. 自サイト内遷移: previousPath から origin + previousPath
  // 3. それ以外は空
  const isFromRoot = previousPath == null || previousPath === '' || previousPath === '/';
  let previousPageUrl = '';
  if (externalReferrer && !isSameOriginReferrer(externalReferrer) && isFromRoot) {
    previousPageUrl = externalReferrer;
  } else if (previousPath && previousPath !== '/' && previousPath !== '') {
    previousPageUrl = window.location.origin + previousPath;
  }
  
  const currentPageUrl = window.location.origin + pagePath;
  
  
  try {
    // Cognito認証状態をチェック（ログイン済みは送信しない）
    const isAuthenticated = await isUserAuthenticated();
    if (isAuthenticated) {
      return;
    }

    // ボディ: 常に page_path。参照元はヘッダーが落ちた場合のフォールバックとしても送る
    const body: Record<string, string> = { page_path: pagePath };
    if (previousPageUrl) {
      body.referrer = previousPageUrl;
    }
    if (getAnalyticsConsent() === true) {
      try {
        const uaData = navigator.userAgentData;
        if (uaData?.getHighEntropyValues) {
          const values = await uaData.getHighEntropyValues(['model']);
          if (values?.model && typeof values.model === 'string') {
            body.device_model = values.model;
          }
        }
      } catch {
        // 機種名取得に失敗しても送信は続行
      }
    }
    
    // ヘッダーを準備
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Current-Page': currentPageUrl,
      'X-Previous-Page': previousPageUrl,
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
    });
    
    
    if (!response.ok) {
      // エラーはログに記録するが、ユーザーには影響を与えない
      const errorText = await response.text();
      console.warn('Failed to track page view:', errorText);
      return;
    }
    
    // 成功時は何も返さない
    return;
  } catch (error) {
    // ネットワークエラーなどはログに記録するが、ユーザーには影響を与えない
    console.warn('Error tracking page view:', error);
    return;
  }
};

/**
 * アナリティクスデータのレスポンス型
 */
export interface AnalyticsResponse {
  total_views: number;
  /** ユニークユーザー数（匿名化IPの種類数で近似） */
  unique_visitors?: number;
  top_pages: Array<{
    page_path: string;
    title?: string;  // ページタイトル（Lambda側で取得）
    views: number;
  }>;
  top_countries: Array<{
    country: string;
    views: number;
  }>;
  period: {
    start: string;
    end: string;
  };
}

/**
 * イベント1件の型（詳細アクセス履歴用）
 */
export interface AnalyticsEventItem {
  event_id?: string;
  timestamp: string;
  page_path: string;
  /** 言語別ページタイトル（APIで解決、未設定時は page_path を表示） */
  title?: string;
  country?: string;
  country_region_name?: string;
  city?: string;
  referrer?: string;
  os?: string;
  device_type?: string;
  browser?: string;
  device_model?: string;
}

/**
 * イベント一覧APIのレスポンス型
 */
/** 円グラフ用の次元別集計（name + count） */
export type DimensionCountItem = { name: string; count: number };

export interface AnalyticsEventsResponse {
  events: AnalyticsEventItem[];
  total: number;
  /** 期間内のユニークな page_path の数 */
  unique_page_count?: number;
  /** 期間内のユニーク訪問者数（匿名化IPの種類数） */
  unique_visitors?: number;
  /** 最多アクセス国（国名） */
  top_country?: string;
  daily_counts: Array<{ date: string; count: number }>;
  /** 国・OS・デバイス・ブラウザ・機種・参照元の集計（円グラフ用。参照元はドメイン単位） */
  dimension_counts?: {
    country?: DimensionCountItem[];
    os?: DimensionCountItem[];
    device_type?: DimensionCountItem[];
    browser?: DimensionCountItem[];
    device_model?: DimensionCountItem[];
    referrer?: DimensionCountItem[];
  };
  period: {
    start: string;
    end: string;
  };
}

/**
 * アナリティクスデータを取得する
 * 
 * @param days 取得する日数（デフォルト: 7日）
 * @param lang 言語コード（'ja' または 'en'、デフォルト: 'ja'）
 * @returns アナリティクスデータ
 */
export const getAnalytics = async (days: number = 7, lang: 'ja' | 'en' = 'ja'): Promise<AnalyticsResponse> => {
  const endpoint = getApiEndpoint();
  const url = `${endpoint}/analytics?days=${days}&lang=${lang}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let analyticsData: AnalyticsResponse;
    if (data.body) {
      analyticsData = typeof data.body === 'string' 
        ? JSON.parse(data.body) 
        : data.body;
    } else {
      analyticsData = data;
    }
    
    return analyticsData;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('CORSエラー: API GatewayのCORS設定を確認してください。');
    }
    
    throw error;
  }
};

/**
 * アクセス解析イベント一覧を取得する（詳細アクセス履歴画面用）
 *
 * @param days 取得する日数（1-30、デフォルト: 7）
 * @param limit 1ページあたりの件数（デフォルト: 50）
 * @param offset オフセット（デフォルト: 0）
 * @param lang 言語（タイトル解決用、デフォルト: 'ja'）
 * @returns イベント一覧と総数・期間
 */
export const getAnalyticsEvents = async (
  days: number = 7,
  limit: number = 50,
  offset: number = 0,
  lang: 'ja' | 'en' = 'ja',
): Promise<AnalyticsEventsResponse> => {
  const endpoint = getApiEndpoint();
  const url = `${endpoint}/analytics?mode=events&days=${days}&limit=${limit}&offset=${offset}&lang=${lang}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = JSON.parse(errorText);
      if (data.message) message = data.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const data = await response.json();
  let body: AnalyticsEventsResponse;
  if (data.body) {
    body = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
  } else {
    body = data;
  }
  return body;
};
